import {
  Accordion,
  AccordionSummary,
  Alert,
  Box,
  Checkbox,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { FORMS_DATA_SETTINGS } from "../../config/urls";
import {
  ColorsConstants,
  borderColors,
  dashboardCardBgColor,
  grayFontColor,
} from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import AppChip from "../../common/app-chip";
import AppDropdown from "../../common/app-dropdown";
import AppTable from "../../common/app-table";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import PageLoader from "../../common/page-loader";

const NEW_ATTRIBUTE = "__new";
const MODE_OPTIONS = [
  { label: "Overwrite", value: "overwrite" },
  { label: "Keep existing", value: "keep" },
];

// Delivery options that aren't available yet; shown so the layout is final.
const COMING_SOON_SECTIONS = [
  { title: "Also send to", summary: "Webhook, Google Sheets, HubSpot, Notify team" },
  { title: "Test submission", summary: "Try sample answers" },
  { title: "How answers come back", summary: "Standard" },
  { title: "Privacy", summary: "Encryption, Inbox visibility, retention" },
  { title: "Stored submission record", summary: "JSON preview" },
];

const toAttributeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);

const isValidAttributeName = (name) => /^[a-z][a-z0-9_]{0,49}$/.test(name);

const emptyMapping = { attribute: "", mode: "overwrite", sensitive: false };

/**
 * Data & destinations tab of a form.
 * `onControlsChange({ dirty, saving, save })` lets the page header drive Save changes.
 */
const FormDataDestinations = ({ formId, onControlsChange }) => {
  const tenant = localStorage.getItem("tenant");
  const { getValidToken } = useAuth();
  const tokenRef = useRef(getValidToken);
  tokenRef.current = getValidToken;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ message: "", severity: "error" });
  const [fields, setFields] = useState([]);
  const [mappings, setMappings] = useState({});
  const [customAttributes, setCustomAttributes] = useState([]);
  const [standardAttributes, setStandardAttributes] = useState([]);
  const [editable, setEditable] = useState(false);
  const [saved, setSaved] = useState("");
  const [newAttr, setNewAttr] = useState({ key: null, value: "", error: "" });

  const snapshot = JSON.stringify({ mappings, customAttributes });
  const dirty = !loading && snapshot !== saved;

  const getHeaders = useCallback(async () => {
    const token = await tokenRef.current();
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [tenant]);

  const applyResponse = (data) => {
    setFields(data.fields);
    setStandardAttributes(data.standard_attributes);
    setCustomAttributes(data.custom_attributes);
    setEditable(data.editable);
    const full = {};
    data.fields.forEach((f) => {
      full[f.key] = { ...emptyMapping, ...(data.mappings[f.key] || {}) };
    });
    setMappings(full);
    setSaved(
      JSON.stringify({ mappings: full, customAttributes: data.custom_attributes }),
    );
  };

  const readError = async (response, fallback) => {
    const data = await response.json().catch(() => ({}));
    return data?.error || data?.detail || fallback;
  };

  useEffect(() => {
    if (!formId) return;
    (async () => {
      try {
        const response = await fetch(FORMS_DATA_SETTINGS(formId), {
          headers: await getHeaders(),
        });
        if (!response.ok) throw new Error(await readError(response, "Failed to load settings"));
        applyResponse(await response.json());
      } catch (error) {
        setAlert({ message: error.message || "Failed to load settings", severity: "error" });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const response = await fetch(FORMS_DATA_SETTINGS(formId), {
        method: "PUT",
        headers: await getHeaders(),
        body: JSON.stringify({ mappings, custom_attributes: customAttributes }),
      });
      if (!response.ok) throw new Error(await readError(response, "Failed to save"));
      applyResponse(await response.json());
      setAlert({ message: "Changes saved", severity: "success" });
    } catch (error) {
      setAlert({ message: error.message || "Failed to save", severity: "error" });
    } finally {
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, mappings, customAttributes, getHeaders]);

  useEffect(() => {
    onControlsChange?.({ dirty: dirty && editable, saving, save });
  }, [dirty, editable, saving, save, onControlsChange]);

  const allAttributes = [...standardAttributes, ...customAttributes];

  const updateMapping = (key, patch) =>
    setMappings((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const addAttribute = () => {
    const name = toAttributeName(newAttr.value);
    if (!isValidAttributeName(name)) {
      setNewAttr((prev) => ({
        ...prev,
        error: "Start with a letter; use lowercase letters, numbers and underscores.",
      }));
      return;
    }
    if (!allAttributes.includes(name)) setCustomAttributes((prev) => [...prev, name]);
    updateMapping(newAttr.key, { attribute: name });
    setNewAttr({ key: null, value: "", error: "" });
  };

  // Maps every unmapped field to the attribute of the same name, creating it if needed.
  const autoMapAll = () => {
    const created = [];
    const next = { ...mappings };
    fields.forEach((f) => {
      if (next[f.key]?.attribute) return;
      const name = toAttributeName(f.key);
      if (!isValidAttributeName(name)) return;
      if (!allAttributes.includes(name) && !created.includes(name)) created.push(name);
      next[f.key] = { ...next[f.key], attribute: name };
    });
    setMappings(next);
    if (created.length) setCustomAttributes((prev) => [...prev, ...created]);
  };

  const bodyText = { ...typography.bodyLarge, color: "#1D2330" };

  const columns = [
    {
      id: "field",
      label: "Field",
      render: (f) => (
        <Stack spacing={0.25}>
          <Typography sx={bodyText}>{f.label || f.key}</Typography>
          <Box>
            <AppChip label={f.key} sx={{ height: 20, backgroundColor: dashboardCardBgColor }} />
          </Box>
        </Stack>
      ),
    },
    {
      id: "attribute",
      label: "Contact attribute",
      render: (f) => {
        if (newAttr.key === f.key) {
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  value={newAttr.value}
                  autoFocus
                  placeholder="attribute_name"
                  inputProps={{ "aria-label": "New attribute name", maxLength: 50 }}
                  onChange={(e) => setNewAttr({ ...newAttr, value: e.target.value, error: "" })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addAttribute();
                    if (e.key === "Escape") setNewAttr({ key: null, value: "", error: "" });
                  }}
                />
                <ButtonOne title="Add" onClick={addAttribute} />
                <ButtonTwo
                  title="Cancel"
                  onClick={() => setNewAttr({ key: null, value: "", error: "" })}
                />
              </Stack>
              {newAttr.error && (
                <Typography sx={{ ...typography.bodySmall, color: "#b50000" }}>
                  {newAttr.error}
                </Typography>
              )}
            </Stack>
          );
        }
        const current = mappings[f.key]?.attribute || "";
        return (
          <AppDropdown
            disabled={!editable}
            menuItems={[
              { label: "Don't save", value: "" },
              ...allAttributes.map((a) => ({ label: a, value: a })),
              { label: "+ Create new attribute…", value: NEW_ATTRIBUTE },
            ]}
            selectedValue={current}
            onMenuSelect={(value) => {
              if (value === NEW_ATTRIBUTE) {
                setNewAttr({ key: f.key, value: toAttributeName(f.key), error: "" });
              } else {
                updateMapping(f.key, { attribute: value });
              }
            }}
            endIcon={<ExpandMore />}
            sx={{ minWidth: 200, justifyContent: "space-between" }}
          >
            {current || "Don't save"}
          </AppDropdown>
        );
      },
    },
    {
      id: "mode",
      label: "If value exists",
      render: (f) => (
        <AppDropdown
          disabled={!editable || !mappings[f.key]?.attribute}
          menuItems={MODE_OPTIONS}
          selectedValue={mappings[f.key]?.mode || "overwrite"}
          onMenuSelect={(value) => updateMapping(f.key, { mode: value })}
          endIcon={<ExpandMore />}
          sx={{ minWidth: 160, justifyContent: "space-between" }}
        >
          {MODE_OPTIONS.find((o) => o.value === (mappings[f.key]?.mode || "overwrite"))?.label}
        </AppDropdown>
      ),
    },
    {
      id: "sensitive",
      label: "Sensitive",
      render: (f) => (
        <Stack direction="row" alignItems="center">
          <Checkbox
            size="small"
            disabled={!editable}
            checked={Boolean(mappings[f.key]?.sensitive)}
            onChange={(e) => updateMapping(f.key, { sensitive: e.target.checked })}
            inputProps={{ "aria-label": `${f.label || f.key} is sensitive` }}
          />
          <LockOutlinedIcon sx={{ fontSize: 16, color: grayFontColor }} />
        </Stack>
      ),
    },
  ];

  if (loading) return <PageLoader />;

  return (
    <Box>
      {alert.message && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert({ message: "", severity: "error" })}
          sx={{ mb: 2, "& .MuiAlert-message": typography.bodyLarge }}
        >
          {alert.message}
        </Alert>
      )}

      <Box
        sx={{
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box sx={{ p: 2.25, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ ...typography.h5, fontSize: 16 }}>
              Save answers to contact
            </Typography>
            <Box
              component="button"
              type="button"
              disabled={!editable || fields.length === 0}
              onClick={autoMapAll}
              sx={{
                ...typography.bodySmall,
                background: "none",
                border: 0,
                p: 0,
                cursor: "pointer",
                color: ColorsConstants.primaryPurple100,
                "&:disabled": { color: grayFontColor, cursor: "default" },
              }}
            >
              Auto-map all
            </Box>
          </Stack>
          <Typography sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}>
            Every submission is always stored in BotBridge. Mapping also updates the contact.
          </Typography>
          <Alert severity="info" sx={{ mt: 1.5, "& .MuiAlert-message": typography.bodySmall }}>
            Mappings are saved with the form. Contacts aren't updated from them yet.
          </Alert>
        </Box>

        {!loading && fields.length === 0 ? (
          <Typography sx={{ ...typography.bodyLarge, color: grayFontColor, p: 2.25 }}>
            Add input fields in Build screens to map them here.
          </Typography>
        ) : (
          <AppTable
            columns={columns}
            rows={fields}
            rowKey="key"
            loading={loading}
            maxHeight={500}
            autoAdjustHeight={false}
            headCellSx={{
              ...typography.bodySmall,
              backgroundColor: dashboardCardBgColor,
              color: grayFontColor,
              padding: "9px 18px",
              borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
            }}
            bodyCellSx={{ padding: "10px 18px" }}
            rowSx={() => ({
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: "#FFFFFF" },
              "& td": {
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
                borderTop: "none",
              },
              "&:last-child td": { borderBottom: "none" },
            })}
            paperSx={{ border: "none", borderRadius: 0, boxShadow: "none", mb: 0 }}
          />
        )}
      </Box>

      <Box
        sx={{
          border: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        {COMING_SOON_SECTIONS.map((section) => (
          <Accordion
            key={section.title}
            disabled
            disableGutters
            square
            elevation={0}
            sx={{
              "&:not(:last-child)": {
                borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}`,
              },
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2.25 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: "100%" }}>
                <Typography sx={{ ...typography.bodyLarge, fontWeight: 500 }}>
                  {section.title}
                </Typography>
                <AppChip label="Coming soon" sx={{ height: 20 }} />
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: grayFontColor,
                    flex: 1,
                    textAlign: "right",
                    pr: 1,
                  }}
                  noWrap
                >
                  {section.summary}
                </Typography>
              </Stack>
            </AccordionSummary>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default FormDataDestinations;
